import {
  FENIX_BACKUP_FORMAT_VERSION,
} from '../domain/backup.ts';

import type {
  BackupFile,
  BackupResult,
  BackupTimeContext,
  FenixBackupV1,
} from '../domain/backup.ts';

import type {
  BackupRepository,
} from '../ports/backup-repository.ts';

export interface BackupExportEnvironment {
  producerAppVersion: string;

  backupId(): string;

  now(): {
    exportedAt: string;
    timeContext: BackupTimeContext;
  };
}

function invalid():
  BackupResult<never> {
  return {
    ok: false,
    code: 'VALIDATION_ERROR',
  };
}

function validClock(
  exportedAt: string,
  context: BackupTimeContext,
) {
  return (
    Number.isFinite(Date.parse(exportedAt))
    && context.zoneId.trim().length > 0
    && Number.isInteger(context.utcOffsetMinutes)
    && context.utcOffsetMinutes >= -840
    && context.utcOffsetMinutes <= 840
  );
}

export class BackupExport {
  private readonly repository: BackupRepository;
  private readonly environment: BackupExportEnvironment;

  constructor(
    repository: BackupRepository,
    environment: BackupExportEnvironment,
  ) {
    this.repository = repository;
    this.environment = environment;
  }

  async createFile():
    Promise<BackupResult<BackupFile>> {
    const snapshot =
      await this.repository.readLogicalSnapshot();

    if (!snapshot.ok) {
      return snapshot;
    }

    const backupId =
      this.environment.backupId();

    const clock =
      this.environment.now();

    if (
      backupId.trim().length === 0
      || this.environment.producerAppVersion.trim().length === 0
      || !validClock(
        clock.exportedAt,
        clock.timeContext,
      )
    ) {
      return invalid();
    }

    const backup: FenixBackupV1 = {
      backupFormatVersion:
        FENIX_BACKUP_FORMAT_VERSION,

      schemaVersion:
        snapshot.value.schemaVersion,

      producerAppVersion:
        this.environment.producerAppVersion,

      backupId,

      exportedAt:
        clock.exportedAt,

      timeContext:
        clock.timeContext,

      dataset:
        snapshot.value.dataset,

      revisionHistory:
        snapshot.value.revisionHistory,

      mediaManifest:
        snapshot.value.mediaManifest,
    };

    const json =
      JSON.stringify(
        backup,
        null,
        2,
      ) + '\n';

    const safeInstant =
      clock.exportedAt.replace(
        /[:.]/g,
        '-',
      );

    return {
      ok: true,
      value: {
        backup,
        json,
        filename:
          `FENIX-backup-${safeInstant}.json`,
      },
    };
  }
}